import { DEFAULT_DESIGN_SYSTEM } from "@/core/constants";
import { getDesignSystemVariable } from "@/shared";
import ColorPicker from "@components/color-picker";
import Icon from "@components/ui/icon";
import Popover from "@components/ui/popover";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { cn } from "@lib/cn";
import ColorSelect from "@pages/editor/components/color-select";
import { useAppSelector } from "@store";
import { getDesignSystem } from "@store/story/selectors";
import { useEffect, useState } from "react";
import { useMediaLibDialogCtx } from "../../../ctx";
import { useAiImageStoreShallow } from "../store";

export const AiImageColorsSelect = () => {
  const [open, setOpen] = useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [parent] = useAutoAnimate();
  const designSystem = useAppSelector(getDesignSystem);
  const { source } = useMediaLibDialogCtx();
  const { colors, colorsMode, setColors, setColorsMode } =
    useAiImageStoreShallow((s) => ({
      colors: s.colors,
      colorsMode: s.colorsMode,
      setColors: s.setColors,
      setColorsMode: s.setColorsMode,
    }));

  useEffect(() => {
    const design = designSystem ?? DEFAULT_DESIGN_SYSTEM;
    if (design) {
      setColors([
        getDesignSystemVariable(design.primaryColor),
        getDesignSystemVariable(design.secondaryColor),
        getDesignSystemVariable(design.themeColor3),
        getDesignSystemVariable(design.themeColor4),
      ]);
    }
  }, [designSystem, setColors]);

  return (
    <Popover
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) setIsColorPickerOpen(false);
      }}
      placement="left"
    >
      <Popover.Trigger asChild>
        <button className="tw-w-full tw-text-left tw-px-3 tw-h-8 tw-justify-between tw-flex tw-items-center tw-rounded-lg tw-text-xs tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50">
          Colors
          <span className="tw-flex tw-items-center tw-gap-2 tw-text-blue-500">
            {colorsMode === "auto" ? (
              "Automatic"
            ) : (
              <PickedColors colors={colors} />
            )}
            <Icon icon="ri-arrow-right-s-line" className="tw-text-blue-700" />
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Content className="tw-z-[99999] tw-p-1">
        <div className="tw-rounded-xl tw-bg-white tw-shadow-popover tw-border tw-border-solid tw-border-blue/5">
          <div ref={parent}>
            {!isColorPickerOpen ? (
              <div className="tw-p-1">
                <button
                  aria-label="Automatic"
                  onClick={() => {
                    setColorsMode("auto");
                    setOpen(false);
                  }}
                  type="button"
                  className={cn(
                    "tw-w-full tw-text-left tw-px-3 tw-h-8 tw-mb-0.5 tw-flex tw-items-center tw-rounded-lg tw-text-xs tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
                    {
                      "tw-bg-primary-50 hover:tw-bg-50": colorsMode === "auto",
                    },
                  )}
                >
                  Automatic
                </button>
                <button
                  aria-label="Custom colors"
                  onClick={() => {
                    setColorsMode("manual");
                    setIsColorPickerOpen(true);
                  }}
                  type="button"
                  className={cn(
                    "tw-w-full tw-text-left tw-px-3 tw-justify-between tw-h-9 tw-flex tw-items-center tw-rounded-lg tw-text-xs tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
                    {
                      "tw-bg-primary-50 hover:tw-bg-50":
                        colorsMode === "manual",
                    },
                  )}
                >
                  Custom colors
                  <PickedColors colors={colors} />
                </button>
              </div>
            ) : (
              <ColorsPicker
                source={source}
                colors={colors}
                closeColorsPicker={() => setIsColorPickerOpen(false)}
              />
            )}
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
};

const ColorsPicker = ({
  colors,
  closeColorsPicker,
  source,
}: {
  colors: string[];
  closeColorsPicker: () => void;
  source: "default" | "settings";
}) => {
  const { setColors } = useAiImageStoreShallow((s) => ({
    setColors: s.setColors,
  }));
  return (
    <div className="tw-px-1 tw-py-4">
      <div className="tw-flex tw-items-center tw-px-4 tw-pb-4 tw-border-b tw-border-solid tw-border-blue-100 tw-text-blue tw-font-sans tw-font-semibold tw-text-sm tw-justify-between">
        <button
          aria-label="Back"
          type="button"
          className="tw-flex tw-items-center tw-justify-center tw-cursor-pointer hover:tw-opacity-70 tw-transition-opacity"
          onClick={closeColorsPicker}
        >
          <Icon icon="ri-arrow-left-line" className="tw-text-blue-500" />
        </button>
        Custom colors
        <span className="tw-block tw-size-4"></span>
      </div>
      <div className="tw-pt-4 tw-space-y-2">
        {colors.map((color, index) => (
          <div
            key={index}
            className="tw-flex tw-items-center tw-px-4 tw-gap-10"
          >
            <span className="tw-font-sans tw-text-xs tw-font-medium tw-text-blue">
              Color #{index + 1}
            </span>
            <Popover>
              <Popover.Trigger asChild>
                <div className="tw-h-8 tw-pl-1.5 tw-pr-2.5 tw-grow tw-cursor-pointer tw-flex tw-items-center tw-gap-1.5 tw-border tw-border-solid tw-border-blue-100 tw-rounded-lg">
                  <span
                    className="tw-size-5 tw-block tw-rounded-sm tw-border tw-border-solid tw-border-blue/10 tw-shadow-sm"
                    style={{ backgroundColor: color }}
                  ></span>
                  <span className="tw-font-sans tw-text-xs tw-font-normal tw-text-blue">
                    {color}
                  </span>
                </div>
              </Popover.Trigger>
              <Popover.Content className="tw-z-[99999] tw-p-1">
                {source === "settings" ? (
                  <div className="tw-rounded-xl tw-z-[99999] tw-bg-white tw-shadow-popover tw-border tw-border-solid tw-border-blue/5">
                    <ColorPicker
                      current={color}
                      onColorAdd={(color) => {
                        setColors(
                          colors.map((c, i) =>
                            i === index ? getDesignSystemVariable(color) : c,
                          ),
                        );
                        closeColorsPicker();
                      }}
                      onGoBack={closeColorsPicker}
                    />
                  </div>
                ) : (
                  <ColorSelect
                    current={color}
                    onColorSelect={(color) => {
                      setColors(
                        colors.map((c, i) =>
                          i === index ? getDesignSystemVariable(color) : c,
                        ),
                      );
                    }}
                  />
                )}
              </Popover.Content>
            </Popover>
          </div>
        ))}
      </div>
    </div>
  );
};

const PickedColors = ({ colors }: { colors: string[] }) => {
  return (
    <div className="tw-flex tw-items-center tw-h-5 tw-rounded-[36px] tw-overflow-hidden tw-border-blue/10 tw-border tw-border-solid">
      <span
        className="tw-size-5 tw-block"
        style={{ backgroundColor: colors[0] }}
      ></span>
      <span
        className="tw-size-5 tw-block"
        style={{ backgroundColor: colors[1] }}
      ></span>
      <span
        className="tw-size-5 tw-block"
        style={{ backgroundColor: colors[2] }}
      ></span>
      <span
        className="tw-size-5 tw-block"
        style={{ backgroundColor: colors[3] }}
      ></span>
    </div>
  );
};
